"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateRgbppTokenInfoSize = exports.encodeRgbppTokenInfo = exports.estimateWitnessSize = exports.buildSpvClientCellDep = exports.transformSpvProof = exports.isBtcTimeLockCell = exports.isRgbppLockCell = exports.replaceLockArgsWithRealBtcTxId = exports.compareInputs = exports.buildPreLockArgs = exports.buildRgbppLockArgs = exports.isRgbppLockOrBtcTimeLock = exports.btcTxIdFromBtcTimeLockArgs = exports.lockScriptFromBtcTimeLockArgs = exports.calculateCommitment = exports.genBtcTimeLockScript = exports.genBtcTimeLockArgs = exports.genRgbppLockScript = void 0;
const js_sha256_1 = require("js-sha256");
const hex_1 = require("./hex");
const constants_1 = require("../constants");
const ckb_sdk_utils_1 = require("@nervosnetwork/ckb-sdk-utils");
const rgbpp_1 = require("../schemas/generated/rgbpp");
const blockchain_1 = require("../schemas/generated/blockchain");
const base_1 = require("@ckb-lumos/base");
const codec_1 = require("@ckb-lumos/codec");
const case_parser_1 = require("./case-parser");
const error_1 = require("../error");
const genRgbppLockScript = (rgbppLockArgs, isMainnet) => {
    return Object.assign(Object.assign({}, (0, constants_1.getRgbppLockScript)(isMainnet)), { args: (0, hex_1.append0x)(rgbppLockArgs) });
};
exports.genRgbppLockScript = genRgbppLockScript;
const genBtcTimeLockArgs = (lock, btcTxId, after) => {
    const btcTxid = base_1.blockchain.Byte32.pack((0, hex_1.reverseHex)(btcTxId));
    const lockScript = blockchain_1.Script.unpack((0, ckb_sdk_utils_1.serializeScript)(lock));
    return (0, ckb_sdk_utils_1.bytesToHex)(rgbpp_1.BTCTimeLock.pack({ lockScript, after, btcTxid }));
};
exports.genBtcTimeLockArgs = genBtcTimeLockArgs;
/**
 * table BTCTimeLock {
    lock_script: Script,
    after: Uint32,
    btc_txid: Byte32,
  }
 */
const genBtcTimeLockScript = (toLock, isMainnet) => {
    const args = (0, exports.genBtcTimeLockArgs)(toLock, constants_1.RGBPP_TX_ID_PLACEHOLDER, constants_1.BTC_JUMP_CONFIRMATION_BLOCKS);
    return Object.assign(Object.assign({}, (0, constants_1.getBtcTimeLockScript)(isMainnet)), { args });
};
exports.genBtcTimeLockScript = genBtcTimeLockScript;
// The maximum length of inputs and outputs is 255, and the field type representing the length in the RGB++ protocol is Uint8
const MAX_RGBPP_CELL_NUM = 255;
// refer to https://github.com/ckb-cell/rgbpp/blob/0c090b039e8d026aad4336395b908af283a70ebf/contracts/rgbpp-lock/src/main.rs#L173-L211
const calculateCommitment = (rgbppVirtualTx) => {
    var hash = js_sha256_1.sha256.create();
    hash.update((0, ckb_sdk_utils_1.hexToBytes)((0, hex_1.utf8ToHex)('RGB++')));
    const version = [0, 0];
    hash.update(version);
    const { inputs, outputs, outputsData } = rgbppVirtualTx;
    if (inputs.length > MAX_RGBPP_CELL_NUM || outputs.length > MAX_RGBPP_CELL_NUM) {
        throw new error_1.InputsOrOutputsLenError('The inputs or outputs length of RGB++ CKB virtual tx cannot be greater than 255');
    }
    hash.update([inputs.length, outputs.length]);
    for (const input of inputs) {
        hash.update((0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.serializeOutPoint)(input.previousOutput)));
    }
    for (let index = 0; index < outputs.length; index++) {
        const output = outputs[index];
        const outputData = outputsData[index];
        hash.update((0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.serializeOutput)(output)));
        const outputDataLen = (0, hex_1.u32ToLe)((0, hex_1.remove0x)(outputData).length / 2);
        hash.update((0, ckb_sdk_utils_1.hexToBytes)((0, hex_1.append0x)(outputDataLen)));
        hash.update((0, ckb_sdk_utils_1.hexToBytes)(outputData));
    }
    // double sha256
    return (0, js_sha256_1.sha256)(hash.array());
};
exports.calculateCommitment = calculateCommitment;
/**
 * table BTCTimeLock {
    lock_script: Script,
    after: Uint32,
    btc_txid: Byte32,
  }
 */
const lockScriptFromBtcTimeLockArgs = (args) => {
    const { lockScript } = rgbpp_1.BTCTimeLock.unpack((0, hex_1.append0x)(args));
    return Object.assign(Object.assign({}, lockScript), { args: codec_1.bytes.hexify(base_1.blockchain.Bytes.unpack(lockScript.args)) });
};
exports.lockScriptFromBtcTimeLockArgs = lockScriptFromBtcTimeLockArgs;
const btcTxIdFromBtcTimeLockArgs = (args) => {
    const btcTimeLockArgs = rgbpp_1.BTCTimeLock.unpack((0, hex_1.append0x)(args));
    return (0, hex_1.reverseHex)((0, hex_1.append0x)(btcTimeLockArgs.btcTxid));
};
exports.btcTxIdFromBtcTimeLockArgs = btcTxIdFromBtcTimeLockArgs;
const isRgbppLockOrBtcTimeLock = (lock, isMainnet) => {
    const rgbppLock = (0, constants_1.getRgbppLockScript)(isMainnet);
    const isRgbppLock = lock.codeHash === rgbppLock.codeHash && lock.hashType === rgbppLock.hashType;
    const btcTimeLock = (0, constants_1.getBtcTimeLockScript)(isMainnet);
    const isBtcTimeLock = lock.codeHash === btcTimeLock.codeHash && lock.hashType === btcTimeLock.hashType;
    return isRgbppLock || isBtcTimeLock;
};
exports.isRgbppLockOrBtcTimeLock = isRgbppLockOrBtcTimeLock;
/**
 * https://learnmeabitcoin.com/technical/general/byte-order/
 * Whenever you're working with transaction/block hashes internally (e.g. inside raw bitcoin data), you use the natural byte order.
 * Whenever you're displaying or searching for transaction/block hashes, you use the reverse byte order.
 */
const buildRgbppLockArgs = (outIndex, btcTxId) => {
    return `0x${(0, hex_1.u32ToLe)(outIndex)}${(0, hex_1.remove0x)((0, hex_1.reverseHex)(btcTxId))}`;
};
exports.buildRgbppLockArgs = buildRgbppLockArgs;
const buildPreLockArgs = (outIndex) => {
    return (0, exports.buildRgbppLockArgs)(outIndex, constants_1.RGBPP_TX_ID_PLACEHOLDER);
};
exports.buildPreLockArgs = buildPreLockArgs;
const compareInputs = (a, b) => {
    if (a.output.lock.args < b.output.lock.args) {
        return -1;
    }
    if (a.output.lock.args > b.output.lock.args) {
        return 1;
    }
    return 0;
};
exports.compareInputs = compareInputs;
/**
 * RGBPP lock args: out_index | bitcoin_tx_id
 * BTC time lock args: lock_script | after | bitcoin_tx_id
 *
 * https://learnmeabitcoin.com/technical/general/byte-order/
 * Whenever you're working with transaction/block hashes internally (e.g. inside raw bitcoin data), you use the natural byte order.
 * Whenever you're displaying or searching for transaction/block hashes, you use the reverse byte order.
 */
const RGBPP_MIN_LOCK_ARGS_SIZE = 36 * 2;
const BTC_TX_ID_SIZE = 32 * 2;
const replaceLockArgsWithRealBtcTxId = (lockArgs, txId) => {
    const argsLength = (0, hex_1.remove0x)(lockArgs).length;
    if (argsLength < RGBPP_MIN_LOCK_ARGS_SIZE) {
        throw new Error('Rgbpp lock args or BTC time lock args length is invalid');
    }
    return `0x${(0, hex_1.remove0x)(lockArgs).substring(0, argsLength - BTC_TX_ID_SIZE)}${(0, hex_1.remove0x)((0, hex_1.reverseHex)(txId))}`;
};
exports.replaceLockArgsWithRealBtcTxId = replaceLockArgsWithRealBtcTxId;
const isRgbppLockCell = (cell, isMainnet) => {
    const rgbppLock = (0, constants_1.getRgbppLockScript)(isMainnet);
    const isRgbppLock = cell.lock.codeHash === rgbppLock.codeHash && cell.lock.hashType === rgbppLock.hashType;
    return isRgbppLock;
};
exports.isRgbppLockCell = isRgbppLockCell;
const isBtcTimeLockCell = (cell, isMainnet) => {
    const btcTimeLock = (0, constants_1.getBtcTimeLockScript)(isMainnet);
    const isBtcTimeLock = cell.lock.codeHash === btcTimeLock.codeHash && cell.lock.hashType === btcTimeLock.hashType;
    return isBtcTimeLock;
};
exports.isBtcTimeLockCell = isBtcTimeLockCell;
const transformSpvProof = (spvProof) => {
    return (0, case_parser_1.toCamelcase)(spvProof);
};
exports.transformSpvProof = transformSpvProof;
const buildSpvClientCellDep = (spvClient) => {
    const cellDep = {
        outPoint: spvClient,
        depType: 'code',
    };
    return cellDep;
};
exports.buildSpvClientCellDep = buildSpvClientCellDep;
/**
 * Estimate the size of the witness based on the number of groups of lock args
 * @param rgbppLockArgsList The rgbpp assets cell lock script args array whose data structure is: out_index | bitcoin_tx_id
 */
const estimateWitnessSize = (rgbppLockArgsList) => {
    const rgbppLockArgsSet = new Set(rgbppLockArgsList);
    const inputsGroupSize = rgbppLockArgsSet.size;
    return constants_1.RGBPP_TX_WITNESS_MAX_SIZE * inputsGroupSize;
};
exports.estimateWitnessSize = estimateWitnessSize;
/**
 * Encode RGBPP token information into hex format
 * @param tokenInfo RGBPP token information
 * @returns hex string for cell data
 */
const encodeRgbppTokenInfo = (tokenInfo) => {
    const decimal = (0, hex_1.u8ToHex)(tokenInfo.decimal);
    const name = (0, hex_1.remove0x)((0, hex_1.utf8ToHex)(tokenInfo.name));
    const nameSize = (0, hex_1.u8ToHex)(name.length / 2);
    const symbol = (0, hex_1.remove0x)((0, hex_1.utf8ToHex)(tokenInfo.symbol));
    const symbolSize = (0, hex_1.u8ToHex)(symbol.length / 2);
    return `0x${decimal}${nameSize}${name}${symbolSize}${symbol}`;
};
exports.encodeRgbppTokenInfo = encodeRgbppTokenInfo;
const calculateRgbppTokenInfoSize = (tokenInfo) => {
    const encodedTokenInfo = (0, exports.encodeRgbppTokenInfo)(tokenInfo);
    return BigInt((0, hex_1.remove0x)(encodedTokenInfo).length / 2);
};
exports.calculateRgbppTokenInfoSize = calculateRgbppTokenInfoSize;
